/**
 * Companies Analytics Calculator
 * Based on actual Cloud SQL database schema (backend/database.py)
 *
 * =====================================================================
 * COMPANY FIELDS FROM DATABASE - Two Response Formats:
 * =====================================================================
 *
 * 1. Company.to_dict() returns these fields:
 *    | Field                    | Type              | Notes                     |
 *    |--------------------------|-------------------|---------------------------|
 *    | id                       | String (UUID)     | Primary key               |
 *    | pitchbook_id             | String            | Unique ID                 |
 *    | name                     | String            | Company name              |
 *    | description              | String            | Company description       |
 *    | primary_industry         | String            | From primary_industry_code|
 *    | primary_industry_sector  | String            | Industry sector           |
 *    | primary_industry_group   | String            | Industry group            |
 *    | all_industries           | String            | Comma-separated list      |
 *    | verticals                | Array<String>     | E.g., ['Gaming', 'TMT']   |
 *    | keywords                 | Array<String>     | Company keywords          |
 *    | hq_location              | String            | Full location string      |
 *    | hq_city                  | String            | City name                 |
 *    | hq_state                 | String            | State/province            |
 *    | hq_country               | String            | Country name              |
 *    | hq_region                | String            | Global region             |
 *    | total_raised             | String            | Text, needs parsing       |
 *    | total_raised_usd         | Float             | In millions USD           |
 *    | revenue                  | String            | Revenue (text)            |
 *    | year_founded             | String            | Year as string            |
 *    | employees                | String            | Employee count (text)     |
 *    | financing_status         | String            | Current financing status  |
 *    | business_status          | String            | Business status           |
 *    | ownership_status         | String            | Ownership status          |
 *    | active_investors         | Array<String>     | List of investor names    |
 *    | website                  | String            | Company website           |
 *
 * 2. Raw SQL (/api/v1/companies/full) returns additional fields:
 *    | Additional Fields            | Notes                           |
 *    |------------------------------|---------------------------------|
 *    | primary_industry_code        | Raw industry code               |
 *    | hq_global_region             | Full region name                |
 *    | hq_countryterritoryregion    | Full country name               |
 *    | current_business_status      | Full business status            |
 *    | current_financing_status     | Full financing status           |
 *    | competitors                  | Array of competitor names       |
 *    | + many more...               |                                 |
 *
 * NOTE: Field name fallbacks are implemented for both formats.
 * =====================================================================
 */

import {
  countBy,
  getTopN,
  getMostCommon,
  groupByYear,
  getDateRange,
  calculatePercentage,
  createHistogram
} from './chartHelpers';

import {
  removeOutliers,
  applyLogScale,
  calculateLinearRegression,
  shouldUseLogScale,
  normalizeBubbleSizes,
  generateTransformationInfo
} from './dataTransformations';

/**
 * Helper to get field value with fallbacks for different API response formats
 */
const getFieldValue = (company, fieldNames) => {
  for (const field of fieldNames) {
    if (company[field] !== null && company[field] !== undefined && company[field] !== '') {
      return company[field];
    }
  }
  return null;
};

/**
 * Helper to count by field with fallbacks
 */
const countByWithFallback = (array, fieldNames) => {
  const counts = {};
  array.forEach(item => {
    const value = getFieldValue(item, fieldNames);
    if (value !== null && value !== undefined && value !== '') {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return counts;
};

export const calculateCompaniesAnalytics = (companies, allCompanies, options = {}) => {
  if (!companies || companies.length === 0) {
    return {
      summary: { total: 0, cards: [] },
      charts: []
    };
  }

  const { expanded = false } = options;

  const total = companies.length;

  // Use fallbacks for fields that may have different names
  const topIndustry = getMostCommon(companies, 'primary_industry') || getMostCommon(companies, 'primary_industry_code');
  const topRegion = getMostCommon(companies, 'hq_region') || getMostCommon(companies, 'hq_global_region');
  const topFundingStatus = getMostCommon(companies, 'financing_status') || getMostCommon(companies, 'current_financing_status');

  // Calculate companies with year founded data
  const companiesWithYear = companies.filter(c => c.year_founded && !isNaN(c.year_founded));
  const avgYear = companiesWithYear.length > 0
    ? Math.round(companiesWithYear.reduce((sum, c) => sum + parseInt(c.year_founded), 0) / companiesWithYear.length)
    : null;

  // Summary cards
  const summaryCards = [
    {
      label: 'Total Companies',
      value: total.toLocaleString()
    },
    {
      label: 'Avg Founded',
      value: avgYear ? avgYear.toString() : 'N/A'
    },
    {
      label: 'Top Industry',
      value: topIndustry ? truncateText(topIndustry.value, 15) : 'N/A'
    },
    {
      label: 'Top Region',
      value: topRegion ? topRegion.value : 'N/A'
    }
  ];

  // ============================================================
  // Chart 1: Funding Stage Distribution (Pie Chart)
  // ============================================================
  const companiesWithFunding = companies.filter(c => c.financing_status || c.current_financing_status);
  const fundingStatusCounts = countByWithFallback(companies, ['financing_status', 'current_financing_status']);
  const allFundingStatuses = Object.entries(fundingStatusCounts)
    .filter(([name, _]) => name && name !== 'undefined' && name !== 'null')
    .sort((a, b) => b[1] - a[1]);

  // Take top 5 and group rest as "Others"
  const topFundingStatusesRaw = allFundingStatuses.slice(0, 5);
  const fundingOthersCount = allFundingStatuses.slice(5).reduce((sum, [_, count]) => sum + count, 0);
  const topFundingStatuses = topFundingStatusesRaw.map(([name, value]) => ({ name, value }));
  if (fundingOthersCount > 0) {
    topFundingStatuses.push({ name: 'Others', value: fundingOthersCount });
  }

  const fundingFieldUsed = companies.some(c => c.financing_status) ? 'financing_status' : 'current_financing_status';
  const fundingTransformInfo = {
    chartTitle: 'Funding Stage Distribution',
    interpretation: 'Shows the distribution of companies by their current financing status (Seed, Series A, B, C, etc.).',
    procedures: [
      `• Field: ${fundingFieldUsed} (String)`,
      `• Data Type: Categorical string`,
      `• Coverage: ${companiesWithFunding.length}/${total} companies (${((companiesWithFunding.length/total)*100).toFixed(1)}%)`,
      `• Unique Stages: ${allFundingStatuses.length}`,
      `• Transformation: Top 5 shown, rest grouped as "Others"`
    ],
    sampleSize: `${companiesWithFunding.length} companies with funding status`
  };

  // ============================================================
  // Chart 2: Year Founded Distribution (Histogram by decade)
  // ============================================================
  const yearHistogram = groupByYear(companiesWithYear, 'year_founded');
  const decadeData = groupIntoDecades(yearHistogram);

  const yearTransformInfo = {
    chartTitle: 'Companies by Decade',
    interpretation: 'Shows when companies were founded, grouped by decade. Useful for understanding portfolio vintage.',
    procedures: [
      `• Field: year_founded (String → Integer)`,
      `• Data Type: Year parsed to integer`,
      `• Coverage: ${companiesWithYear.length}/${total} companies (${((companiesWithYear.length/total)*100).toFixed(1)}%)`,
      `• Transformation: Grouped into decade buckets (1990s, 2000s, etc.)`
    ],
    sampleSize: `${companiesWithYear.length} companies with founding year`
  };

  // ============================================================
  // Chart 3: Industry Concentration (Horizontal Bar - Top 10) - FIXED
  // ============================================================
  const companiesWithIndustry = companies.filter(c => c.primary_industry || c.primary_industry_code);
  const industryCounts = countByWithFallback(companies, ['primary_industry', 'primary_industry_code']);

  // Debug: Log what we're getting
  console.log('[CompaniesAnalytics] Industry counts:', industryCounts);
  console.log('[CompaniesAnalytics] Sample primary_industry values:', companies.slice(0, 5).map(c => ({
    primary_industry: c.primary_industry,
    primary_industry_code: c.primary_industry_code,
    type: typeof c.primary_industry
  })));

  // Ensure proper string labels, filter out numeric-only values
  const allIndustries = Object.entries(industryCounts)
    .filter(([name, _]) => name && name !== 'undefined' && name !== 'null' && isNaN(Number(name)))
    .sort((a, b) => b[1] - a[1]);

  // Take top 8 and group rest as "Others"
  const topIndustriesRaw = allIndustries.slice(0, 8);
  const industryOthersCount = allIndustries.slice(8).reduce((sum, [_, count]) => sum + count, 0);

  const topIndustries = topIndustriesRaw.map(([name, value]) => ({
    name: String(name).trim() || 'Unknown Industry',
    value: Number(value)
  }));
  if (industryOthersCount > 0) {
    topIndustries.push({ name: 'Others', value: industryOthersCount });
  }

  const industryFieldUsed = companies.some(c => c.primary_industry) ? 'primary_industry' : 'primary_industry_code';
  const industryTransformInfo = {
    chartTitle: 'Top Industries',
    interpretation: 'Shows industry concentration of companies. "Others" combines smaller industry categories for cleaner visualization.',
    procedures: [
      `• Field: ${industryFieldUsed} (String)`,
      `• Data Type: Categorical string`,
      `• Coverage: ${companiesWithIndustry.length}/${total} companies (${((companiesWithIndustry.length/total)*100).toFixed(1)}%)`,
      `• Total Industries: ${allIndustries.length}`,
      `• Transformation: Top 8 shown individually, remaining ${Math.max(0, allIndustries.length - 8)} grouped as "Others"`,
      `• "Others" contains: ${industryOthersCount} companies (${((industryOthersCount/total)*100).toFixed(1)}%)`
    ],
    sampleSize: `${companiesWithIndustry.length} companies with industry classification`,
    issues: topIndustries.length === 0 ? ['⚠️ No valid industry data found - check data format'] : null
  };

  // ============================================================
  // Chart 4: Geographic Distribution - FIXED (same approach as Deals)
  // ============================================================
  const companiesWithCountry = companies.filter(c => c.hq_country || c.hq_region || c.hq_global_region || c.hq_location);

  // Try country first (most specific), fall back to region
  const countryCounts = countByWithFallback(companies, ['hq_country', 'hq_countryterritoryregion']);
  const regionCounts = countByWithFallback(companies, ['hq_region', 'hq_global_region', 'hq_location']);

  const hasCountryData = Object.keys(countryCounts).length > 0;
  const hasRegionData = Object.keys(regionCounts).length > 0;

  // Create geographic data with proper string filtering
  const countryData = Object.entries(countryCounts)
    .filter(([country, _]) => country && country !== 'undefined' && country !== 'null' && isNaN(Number(country)))
    .sort((a, b) => b[1] - a[1])
    .map(([country, count]) => ({
      name: String(country).trim(),
      value: Number(count),
      code: getCountryCode(country)
    }));

  const regionData = Object.entries(regionCounts)
    .filter(([region, _]) => region && region !== 'undefined' && region !== 'null' && isNaN(Number(region)))
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({
      name: String(name).trim(),
      value: Number(value)
    }));

  // Use country data if available, otherwise fall back to region data
  const geoData = countryData.length > 0 ? countryData : regionData;

  const geoFieldUsed = hasCountryData ? 'hq_country' : (hasRegionData ? 'hq_region/hq_global_region' : 'none');
  const geoTransformInfo = {
    chartTitle: 'Geographic Distribution',
    interpretation: 'Shows where companies are headquartered. Useful for identifying geographic concentration.',
    procedures: [
      `• Primary Field: ${geoFieldUsed}`,
      `• Data Type: Categorical string`,
      `• Coverage: ${companiesWithCountry.length}/${total} companies have location data (${((companiesWithCountry.length/total)*100).toFixed(1)}%)`,
      hasCountryData ? `• Countries Found: ${countryData.length}` : '• Countries: Not available',
      hasRegionData ? `• Regions Found: ${regionData.length}` : '• Regions: Not available',
      `• Transformation: Aggregated count by geographic area`
    ],
    sampleSize: `${companiesWithCountry.length} companies with geographic data`,
    issues: geoData.length === 0 ? ['⚠️ No geographic data available'] : null
  };

  // ============================================================
  // Chart 5: Business Status (Pie Chart)
  // ============================================================
  const companiesWithStatus = companies.filter(c => c.business_status || c.current_business_status);
  const businessStatusCounts = countByWithFallback(companies, ['business_status', 'current_business_status']);
  const allBusinessStatuses = Object.entries(businessStatusCounts)
    .filter(([name, _]) => name && name !== 'undefined' && name !== 'null')
    .sort((a, b) => b[1] - a[1]);

  // Take top 5 and group rest as "Others"
  const topBusinessStatusesRaw = allBusinessStatuses.slice(0, 5);
  const businessOthersCount = allBusinessStatuses.slice(5).reduce((sum, [_, count]) => sum + count, 0);
  const topBusinessStatuses = topBusinessStatusesRaw.map(([name, value]) => ({ name, value }));
  if (businessOthersCount > 0) {
    topBusinessStatuses.push({ name: 'Others', value: businessOthersCount });
  }

  const businessFieldUsed = companies.some(c => c.business_status) ? 'business_status' : 'current_business_status';
  const businessTransformInfo = {
    chartTitle: 'Business Status',
    interpretation: 'Current operational status of companies (Operating, Acquired, Out of Business, etc.).',
    procedures: [
      `• Field: ${businessFieldUsed} (String)`,
      `• Data Type: Categorical string`,
      `• Coverage: ${companiesWithStatus.length}/${total} companies (${((companiesWithStatus.length/total)*100).toFixed(1)}%)`,
      `• Unique Statuses: ${allBusinessStatuses.length}`,
      `• Transformation: Top 5 shown, rest grouped as "Others"`
    ],
    sampleSize: `${companiesWithStatus.length} companies with business status`
  };

  // ============================================================
  // Chart 6: Company Maturity (Age Distribution)
  // ============================================================
  const currentYear = new Date().getFullYear();
  const ageData = companiesWithYear.map(c => ({
    age: currentYear - parseInt(c.year_founded)
  }));
  const ageHistogram = createAgeHistogram(ageData);

  const ageTransformInfo = {
    chartTitle: 'Company Age Distribution',
    interpretation: 'Shows the age distribution of companies. Useful for understanding portfolio maturity.',
    procedures: [
      `• Field: year_founded (String → Age calculation)`,
      `• Data Type: Integer (current year - founded year)`,
      `• Coverage: ${companiesWithYear.length}/${total} companies (${((companiesWithYear.length/total)*100).toFixed(1)}%)`,
      `• Transformation: Bucketed into age ranges (0-2, 3-5, 6-10, 11-20, 20+ years)`
    ],
    sampleSize: `${companiesWithYear.length} companies with founding year`
  };

  // ============================================================
  // Build Charts Array
  // ============================================================
  const charts = [
    {
      id: 'funding_stage',
      title: 'Funding Stage Distribution',
      type: 'pie',
      data: topFundingStatuses,
      note: topFundingStatuses.length > 0
        ? `${topFundingStatuses[0]?.name || 'Unknown'} leads (${topFundingStatuses[0] ? calculatePercentage(topFundingStatuses[0].value, total) : 0}%)`
        : '⚠️ No funding status data',
      transformInfo: fundingTransformInfo
    },
    {
      id: 'year_founded',
      title: 'Companies by Decade',
      type: 'bar',
      data: decadeData,
      note: `${companiesWithYear.length} companies with founding data`,
      transformInfo: yearTransformInfo
    },
    {
      id: 'industries',
      title: 'Top Industries',
      type: 'horizontal-bar',
      data: topIndustries,
      note: topIndustries.length > 0
        ? `${topIndustries[0]?.name || 'Unknown'} leads • ${allIndustries.length} total industries`
        : '⚠️ No valid industry data found',
      transformInfo: industryTransformInfo
    },
    {
      id: 'geo_distribution',
      title: 'Geographic Distribution',
      type: 'geo-heatmap',
      data: geoData,
      fallbackData: regionData,
      note: `${geoData.length} locations • ${companiesWithCountry.length} companies with location`,
      transformInfo: geoTransformInfo
    },
    {
      id: 'business_status',
      title: 'Business Status',
      type: 'donut',
      data: topBusinessStatuses,
      note: `${topBusinessStatuses.length - (businessOthersCount > 0 ? 1 : 0)} statuses + Others`,
      transformInfo: businessTransformInfo
    },
    {
      id: 'company_age',
      title: 'Company Age Distribution',
      type: 'bar',
      data: ageHistogram,
      note: `Median age: ${calculateMedianAge(ageData)} years`,
      transformInfo: ageTransformInfo
    }
  ];

  // EXPANDED MODE: Add additional detailed charts
  if (expanded) {
    // Chart 7: Employee Count Distribution
    const employeeRanges = [
      { label: '1-10', min: 1, max: 10 },
      { label: '11-50', min: 11, max: 50 },
      { label: '51-200', min: 51, max: 200 },
      { label: '201-500', min: 201, max: 500 },
      { label: '501-1000', min: 501, max: 1000 },
      { label: '1000+', min: 1001, max: 1000000 }
    ];

    const employeeData = employeeRanges.map(range => ({
      name: range.label,
      value: companies.filter(c => {
        const emp = parseInt(c.employees);
        return emp >= range.min && emp <= range.max;
      }).length
    })).filter(d => d.value > 0);

    if (employeeData.length > 0) {
      charts.push({
        id: 'employee_distribution',
        title: 'Employee Count Distribution',
        type: 'bar',
        data: employeeData,
        note: `${employeeData.reduce((sum, d) => sum + d.value, 0)} companies with employee data`
      });
    }

    // Chart 8: Total Raised Distribution
    const fundingRanges = [
      { label: '<$1M', min: 0, max: 1 },
      { label: '$1M-$10M', min: 1, max: 10 },
      { label: '$10M-$50M', min: 10, max: 50 },
      { label: '$50M-$100M', min: 50, max: 100 },
      { label: '$100M-$500M', min: 100, max: 500 },
      { label: '$500M+', min: 500, max: 1000000 }
    ];

    const fundingData = fundingRanges.map(range => ({
      name: range.label,
      value: companies.filter(c => {
        const raised = parseFloat(c.total_raised || c.total_raised_usd);
        return raised >= range.min && raised < range.max;
      }).length
    })).filter(d => d.value > 0);

    if (fundingData.length > 0) {
      charts.push({
        id: 'funding_distribution',
        title: 'Total Capital Raised Distribution',
        type: 'bar',
        data: fundingData,
        note: `${fundingData.reduce((sum, d) => sum + d.value, 0)} companies with funding data`
      });
    }

    // Chart 9: Growth Rate Distribution (if available)
    if (companies.some(c => c.growth_rate)) {
      const growthRanges = [
        { label: '<0%', min: -1000, max: 0 },
        { label: '0-10%', min: 0, max: 10 },
        { label: '10-25%', min: 10, max: 25 },
        { label: '25-50%', min: 25, max: 50 },
        { label: '50-100%', min: 50, max: 100 },
        { label: '100%+', min: 100, max: 10000 }
      ];

      const growthData = growthRanges.map(range => ({
        name: range.label,
        value: companies.filter(c => {
          const growth = parseFloat(c.growth_rate);
          return growth >= range.min && growth < range.max;
        }).length
      })).filter(d => d.value > 0);

      if (growthData.length > 0) {
        charts.push({
          id: 'growth_distribution',
          title: 'Company Growth Rate Distribution',
          type: 'bar',
          data: growthData,
          note: `${growthData.reduce((sum, d) => sum + d.value, 0)} companies with growth data`
        });
      }
    }

    // Chart 10: Top Competitors Analysis - FIXED
    const competitorCounts = {};
    let companiesWithCompetitors = 0;

    companies.forEach(company => {
      if (company.competitors && Array.isArray(company.competitors) && company.competitors.length > 0) {
        companiesWithCompetitors++;
        company.competitors.forEach(competitor => {
          // Only count valid string competitors, not numbers
          if (competitor && typeof competitor === 'string' && competitor.trim() && isNaN(Number(competitor))) {
            const cleanCompetitor = competitor.trim();
            competitorCounts[cleanCompetitor] = (competitorCounts[cleanCompetitor] || 0) + 1;
          }
        });
      }
    });

    // Filter and sort competitors
    const allCompetitors = Object.entries(competitorCounts)
      .filter(([name, _]) => name && name !== 'undefined' && name !== 'null' && isNaN(Number(name)))
      .sort((a, b) => b[1] - a[1]);

    const topCompetitors = allCompetitors.slice(0, 10).map(([name, value]) => ({
      name: String(name).trim(),
      value: Number(value)
    }));

    const competitorTransformInfo = {
      chartTitle: 'Most Mentioned Competitors',
      interpretation: 'Shows which competitors are most frequently mentioned across company profiles. Higher counts indicate common competitive landscape.',
      procedures: [
        `• Field: competitors (Array<String>)`,
        `• Data Type: Array of competitor company names`,
        `• Coverage: ${companiesWithCompetitors}/${total} companies have competitor data (${((companiesWithCompetitors/total)*100).toFixed(1)}%)`,
        `• Unique Competitors: ${allCompetitors.length}`,
        `• Transformation: Count occurrences, top 10 shown`
      ],
      sampleSize: `${companiesWithCompetitors} companies with competitor data`,
      issues: topCompetitors.length === 0 ? ['⚠️ No valid competitor data found - may not be populated in dataset'] : null
    };

    if (topCompetitors.length > 0) {
      charts.push({
        id: 'top_competitors',
        title: 'Most Mentioned Competitors',
        type: 'horizontal-bar',
        data: topCompetitors,
        note: topCompetitors.length > 0
          ? `${topCompetitors[0]?.name || 'Unknown'} leads • ${allCompetitors.length} unique competitors`
          : '⚠️ No competitor data available',
        transformInfo: competitorTransformInfo
      });
    }

    // ⭐ ENHANCED SCATTER PLOTS - Valuable Insights Despite Sparse Data ⭐

    // Chart 11: Revenue vs Net Income - Profitability Analysis (14% data)
    let profitabilityRaw = companies
      .filter(c => c.revenue && c.net_income && c.employees)
      .map(c => ({
        name: truncateText(c.name, 25),
        x: parseFloat(c.revenue),
        y: parseFloat(c.net_income),
        revenue: parseFloat(c.revenue),
        netIncome: parseFloat(c.net_income),
        bubbleSize: parseFloat(c.employees) // 3rd dimension: company size
      }))
      .filter(d => !isNaN(d.x) && !isNaN(d.y) && d.x > 0)
      .slice(0, 100);

    if (profitabilityRaw.length >= 5) {
      // Remove outliers
      const { cleaned: profitabilityCleaned, stats: outlierStatsProfit } = removeOutliers(
        profitabilityRaw,
        'x',
        2.5
      );

      // Apply log scale (revenue can range widely)
      const profitabilityLog = applyLogScale(profitabilityCleaned, 'x', 'y');

      // Add bubble sizes
      const profitabilityData = normalizeBubbleSizes(profitabilityLog, 'bubbleSize', 30, 120);

      // Calculate regression
      const regression = calculateLinearRegression(profitabilityLog, 'x', 'y');

      const transformInfo = generateTransformationInfo({
        chartId: 'revenue_profit_scatter',
        chartTitle: 'Revenue vs Net Income (Profitability)',
        outlierStats: outlierStatsProfit,
        useLogScale: true,
        regression,
        bubbleField: 'Employee Count',
        sampleSize: `${profitabilityData.length} of ${profitabilityRaw.length} companies`
      });

      charts.push({
        id: 'revenue_profit_scatter',
        title: 'Revenue vs Net Income (Profitability)',
        type: 'scatter',
        data: profitabilityData,
        xLabel: 'Revenue ($M, log₁₀)',
        yLabel: 'Net Income ($M, log₁₀)',
        note: `${profitabilityData.length} companies • 14% data • ${outlierStatsProfit?.outlierCount || 0} outliers removed`,
        dataQuality: 'limited',
        useLogScale: true,
        regression,
        transformInfo
      });
    }

    // Chart 12: Patent Portfolio Analysis - Total vs Active (10% data)
    let patentRaw = companies
      .filter(c => c.total_patent_documents && c.active_patent_documents && c.year_founded)
      .map(c => {
        const currentYear = new Date().getFullYear();
        const yearFounded = parseFloat(c.year_founded);
        const age = currentYear - yearFounded;
        return {
          name: truncateText(c.name, 25),
          x: parseFloat(c.total_patent_documents),
          y: parseFloat(c.active_patent_documents),
          totalPatents: parseFloat(c.total_patent_documents),
          activePatents: parseFloat(c.active_patent_documents),
          bubbleSize: age // 3rd dimension: company age
        };
      })
      .filter(d => !isNaN(d.x) && !isNaN(d.y) && d.x > 0 && d.y > 0)
      .slice(0, 100);

    if (patentRaw.length >= 5) {
      // Remove outliers
      const { cleaned: patentCleaned, stats: outlierStatsPatent } = removeOutliers(
        patentRaw,
        'x',
        2.0
      );

      // No log scale needed for patents (reasonable range)
      const patentData = normalizeBubbleSizes(patentCleaned, 'bubbleSize', 30, 120);

      // Calculate regression
      const regression = calculateLinearRegression(patentData, 'x', 'y');

      const transformInfo = generateTransformationInfo({
        chartId: 'patent_portfolio_scatter',
        chartTitle: 'Patent Portfolio: Total vs Active',
        outlierStats: outlierStatsPatent,
        useLogScale: false,
        regression,
        bubbleField: 'Company Age',
        sampleSize: `${patentData.length} of ${patentRaw.length} companies`
      });

      charts.push({
        id: 'patent_portfolio_scatter',
        title: 'Patent Portfolio: Total vs Active',
        type: 'scatter',
        data: patentData,
        xLabel: 'Total Patents',
        yLabel: 'Active Patents',
        note: `${patentData.length} companies • 10% data • ${outlierStatsPatent?.outlierCount || 0} outliers removed`,
        dataQuality: 'limited',
        useLogScale: false,
        regression,
        transformInfo
      });
    }

    // Chart 13: Company Age vs Employee Count (95% × 7% data)
    let ageEmployeeRaw = companies
      .filter(c => c.year_founded && c.employees && c.total_raised)
      .map(c => {
        const yearFounded = parseFloat(c.year_founded);
        const currentYear = new Date().getFullYear();
        const age = currentYear - yearFounded;
        return {
          name: truncateText(c.name, 25),
          x: age,
          y: parseFloat(c.employees),
          age: age,
          employees: parseFloat(c.employees),
          founded: yearFounded,
          bubbleSize: parseFloat(c.total_raised) || 1 // 3rd dimension: funding raised
        };
      })
      .filter(d => !isNaN(d.x) && !isNaN(d.y) && d.x > 0 && d.x < 100 && d.y > 0)
      .slice(0, 100);

    if (ageEmployeeRaw.length >= 5) {
      // Remove outliers from employee count
      const { cleaned: ageEmployeeCleaned, stats: outlierStatsAge } = removeOutliers(
        ageEmployeeRaw,
        'y',
        2.5
      );

      // Add bubble sizes
      const ageEmployeeData = normalizeBubbleSizes(ageEmployeeCleaned, 'bubbleSize', 30, 120);

      // Calculate regression
      const regression = calculateLinearRegression(ageEmployeeData, 'x', 'y');

      const transformInfo = generateTransformationInfo({
        chartId: 'age_employees_scatter',
        chartTitle: 'Company Age vs Team Size',
        outlierStats: outlierStatsAge,
        useLogScale: false,
        regression,
        bubbleField: 'Total Funding Raised',
        sampleSize: `${ageEmployeeData.length} of ${ageEmployeeRaw.length} companies`
      });

      charts.push({
        id: 'age_employees_scatter',
        title: 'Company Age vs Team Size',
        type: 'scatter',
        data: ageEmployeeData,
        xLabel: 'Company Age (years)',
        yLabel: 'Employees',
        note: `${ageEmployeeData.length} companies • 7% data • ${outlierStatsAge?.outlierCount || 0} outliers removed`,
        dataQuality: 'limited',
        useLogScale: false,
        regression,
        transformInfo
      });
    }

    // Chart 14: Revenue per Employee - Efficiency Analysis
    let efficiencyRaw = companies
      .filter(c => c.revenue && c.employees && c.total_raised)
      .map(c => {
        const revenue = parseFloat(c.revenue);
        const employees = parseFloat(c.employees);
        return {
          name: truncateText(c.name, 25),
          x: employees,
          y: revenue,
          employees: employees,
          revenue: revenue,
          revenuePerEmployee: (revenue / employees),
          bubbleSize: parseFloat(c.total_raised) || 1 // 3rd dimension: funding
        };
      })
      .filter(d => !isNaN(d.x) && !isNaN(d.y) && d.x > 0 && d.y > 0)
      .slice(0, 100);

    if (efficiencyRaw.length >= 5) {
      // Remove outliers from both axes
      const { cleaned: efficiencyCleaned, stats: outlierStatsEff } = removeOutliers(
        efficiencyRaw,
        'y',
        2.5
      );

      // Apply log scale for revenue
      const efficiencyLog = applyLogScale(efficiencyCleaned, 'x', 'y');

      // Add bubble sizes
      const efficiencyData = normalizeBubbleSizes(efficiencyLog, 'bubbleSize', 30, 120);

      // Calculate regression
      const regression = calculateLinearRegression(efficiencyLog, 'x', 'y');

      const transformInfo = generateTransformationInfo({
        chartId: 'efficiency_scatter',
        chartTitle: 'Revenue vs Employee Count (Efficiency)',
        outlierStats: outlierStatsEff,
        useLogScale: true,
        regression,
        bubbleField: 'Total Funding',
        sampleSize: `${efficiencyData.length} of ${efficiencyRaw.length} companies`
      });

      charts.push({
        id: 'efficiency_scatter',
        title: 'Revenue vs Employee Count (Efficiency)',
        type: 'scatter',
        data: efficiencyData,
        xLabel: 'Employees (log₁₀)',
        yLabel: 'Revenue ($M, log₁₀)',
        note: `${efficiencyData.length} companies • ${outlierStatsEff?.outlierCount || 0} outliers removed`,
        dataQuality: 'limited',
        useLogScale: true,
        regression,
        transformInfo
      });
    }
  }

  return {
    summary: {
      total,
      filtered: allCompanies && total !== allCompanies.length ? total : null,
      cards: summaryCards
    },
    charts: charts.filter(chart => chart.data && chart.data.length > 0)
  };
};

// Helper: Group years into decades
const groupIntoDecades = (yearData) => {
  const decades = {};

  yearData.forEach(item => {
    const year = parseInt(item.name);
    if (isNaN(year)) return;

    const decade = Math.floor(year / 10) * 10;
    const decadeLabel = `${decade}s`;
    decades[decadeLabel] = (decades[decadeLabel] || 0) + item.value;
  });

  return Object.entries(decades)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, value]) => ({ name, value }));
};

// Helper: Create age histogram
const createAgeHistogram = (ageData) => {
  const bins = [
    { label: '0-2 years', min: 0, max: 2 },
    { label: '3-5 years', min: 3, max: 5 },
    { label: '6-10 years', min: 6, max: 10 },
    { label: '11-20 years', min: 11, max: 20 },
    { label: '20+ years', min: 21, max: 200 }
  ];

  return bins.map(bin => ({
    name: bin.label,
    value: ageData.filter(d => d.age >= bin.min && d.age <= bin.max).length
  }));
};

// Helper: Calculate median age
const calculateMedianAge = (ageData) => {
  if (ageData.length === 0) return 0;
  const sorted = ageData.map(d => d.age).sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};

// Helper: Truncate text
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Map country names to ISO 3166-1 alpha-2 codes for geographic visualizations
 */
const getCountryCode = (countryName) => {
  if (!countryName) return null;

  const countryMap = {
    // North America
    'United States': 'US', 'United States of America': 'US', 'USA': 'US', 'US': 'US',
    'Canada': 'CA',
    'Mexico': 'MX',

    // Europe
    'United Kingdom': 'GB', 'UK': 'GB', 'Great Britain': 'GB', 'England': 'GB',
    'Germany': 'DE', 'Deutschland': 'DE',
    'France': 'FR',
    'Italy': 'IT',
    'Spain': 'ES',
    'Netherlands': 'NL', 'Holland': 'NL',
    'Switzerland': 'CH',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Ireland': 'IE',
    'Belgium': 'BE',
    'Austria': 'AT',
    'Poland': 'PL',
    'Portugal': 'PT',
    'Czech Republic': 'CZ', 'Czechia': 'CZ',
    'Greece': 'GR',
    'Luxembourg': 'LU',

    // Asia Pacific
    'China': 'CN', 'People\'s Republic of China': 'CN',
    'Japan': 'JP',
    'South Korea': 'KR', 'Korea': 'KR', 'Republic of Korea': 'KR',
    'India': 'IN',
    'Singapore': 'SG',
    'Hong Kong': 'HK',
    'Taiwan': 'TW',
    'Australia': 'AU',
    'New Zealand': 'NZ',
    'Indonesia': 'ID',
    'Malaysia': 'MY',
    'Thailand': 'TH',
    'Vietnam': 'VN',
    'Philippines': 'PH',

    // Middle East
    'Israel': 'IL',
    'United Arab Emirates': 'AE', 'UAE': 'AE',
    'Saudi Arabia': 'SA',

    // Latin America
    'Brazil': 'BR',
    'Argentina': 'AR',
    'Chile': 'CL',
    'Colombia': 'CO',

    // Africa
    'South Africa': 'ZA',
    'Nigeria': 'NG',
    'Kenya': 'KE',
    'Egypt': 'EG'
  };

  // Direct match
  if (countryMap[countryName]) {
    return countryMap[countryName];
  }

  // Case-insensitive match
  const lowerName = countryName.toLowerCase();
  for (const [name, code] of Object.entries(countryMap)) {
    if (name.toLowerCase() === lowerName) {
      return code;
    }
  }

  // Partial match
  for (const [name, code] of Object.entries(countryMap)) {
    if (lowerName.includes(name.toLowerCase()) || name.toLowerCase().includes(lowerName)) {
      return code;
    }
  }

  return null;
};
